import { cloneDeep } from 'lodash';
import React, { useEffect, useState } from 'react';
import { IAmendmentMedicalDetails, IAmendmentPageState } from '..';
import { IAmendmentGeneralData } from '../line-amendment';
import { validateMedicalDetailsField } from './validation';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import { useLazyQuery } from '@apollo/client';
import { getMedicalLovsQuery } from '../queries';
import { getMedicalLovs } from '../utils';
import Loader from '@components/loader/Loader';
import SelectFormField from '@components/form-fields/SelectFormField';
import MultiselectFormField from '@components/form-fields/MultiselectFormField';
import { useGridStyles } from '@styles/sharedStyles';

interface IMedicalDetailsWidgetProps {
  generalData: IAmendmentGeneralData;
  pageState: IAmendmentPageState;
  onPageStateUpdate: (pageState: IAmendmentPageState) => void;
  isDisabled?: boolean;
}

export interface IMedicalLovs {
  classes: Record<string, string>;
  levels: Record<string, string>;
  policyCovers: Record<string, string>;
  cnssOptions: Record<string, string>;
}

const MedicalDetailsWidget: React.FC<IMedicalDetailsWidgetProps> = ({
  generalData,
  pageState,
  onPageStateUpdate,
  isDisabled,
}) => {
  const { classes } = useGridStyles({
    fieldsPerRow: 3,
  });

  const values = pageState.values.medicalDetails;
  const errors = pageState.errors.medicalDetails;
  const touched = pageState.touched.medicalDetails;

  const [lovs, setLovs] = useState<IMedicalLovs>({
    classes: {},
    cnssOptions: {},
    levels: {},
    policyCovers: {},
  });

  const [getMedicalLovsLazy, { loading }] = useLazyQuery(getMedicalLovsQuery);

  const initialize = async () => {
    const { data: lovsData } = await getMedicalLovsLazy({
      variables: {
        selectedPlanID: generalData.plan.id,
      },
    });

    const lists = getMedicalLovs(lovsData);
    setLovs({
      classes: lists.classes,
      cnssOptions: lists.cnssOptions,
      levels: lists.levels,
      policyCovers: lists.policyCovers,
    });
  };

  useEffect(() => {
    initialize();
  }, []);

  const onFieldUpdate = (
    fieldName: keyof IAmendmentMedicalDetails,
    value: unknown,
    touchedField = false
  ) => {
    const newPageState = cloneDeep(pageState);
    (newPageState.values.medicalDetails as any)[fieldName] = value;
    newPageState.errors.medicalDetails[fieldName] = validateMedicalDetailsField(
      fieldName,
      value,
      generalData.line.externalCode
    );
    if (touchedField) {
      newPageState.touched.medicalDetails[fieldName] = true;
    }
    onPageStateUpdate(newPageState);
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <WidgetPaper>
      <WidgetSection title="Medical Details" isFieldsGrid fieldsPerRow={3}>
        <SelectFormField
          key="class"
          name="class"
          title="Class*"
          placeholder="Class"
          value={values.class}
          error={touched.class ? errors.class : ''}
          onChange={(v) => onFieldUpdate('class', v, true)}
          disabled={isDisabled}
          selectOptions={lovs.classes}
          material
        />

        <SelectFormField
          key="level"
          name="level"
          title="Level*"
          placeholder="Level"
          value={values.level}
          error={touched.level ? errors.level : ''}
          onChange={(v) => onFieldUpdate('level', v, true)}
          disabled={isDisabled}
          selectOptions={lovs.levels}
          material
        />

        <SelectFormField
          key="cnss"
          name="cnss"
          title="CNSS*"
          placeholder="CNSS"
          value={values.cnss}
          error={touched.cnss ? errors.cnss : ''}
          onChange={(v) => onFieldUpdate('cnss', v, true)}
          disabled={isDisabled}
          selectOptions={lovs.cnssOptions}
          material
        />

        <MultiselectFormField
          key="policyCover"
          name="policyCover"
          title="Policy Cover*"
          placeholder="Policy Cover"
          values={values.policyCover}
          error={touched.policyCover ? errors.policyCover : ''}
          onChange={(v) => onFieldUpdate('policyCover', v, true)}
          disabled={isDisabled}
          selectOptions={lovs.policyCovers}
          material
          className={classes.fullWidthField}
        />
      </WidgetSection>
    </WidgetPaper>
  );
};

export default MedicalDetailsWidget;
